// Mirrors the Identity contracts the legacy Identity/Users and Identity/Roles
// pages call: Finkit's IdentityUserAppService / IdentityRoleAppService plus
// ABP's IPermissionAppService. Only the fields the SPA consumes are typed.

// Finkit.UsersDtoCostimzation.UserDto — the datatable row. Roles is the
// server-joined list of role names; the grid shows them comma-joined.
export interface UserDatatableDto {
  id: string;
  email: string;
  userName: string;
  roles: string[];
  isActive: boolean;
  emailConfirmed: boolean;
}

// Volo.Abp.Identity.IdentityUserDto (subset used by the edit form + details).
export interface IdentityUserDto {
  id: string;
  userName: string;
  name?: string | null;
  surname?: string | null;
  email: string;
  phoneNumber?: string | null;
  isActive: boolean;
  lockoutEnabled: boolean;
  emailConfirmed: boolean;
  concurrencyStamp?: string | null;
  // ABP extensible-object bag. Finkit adds the required bool "IsApiUser"
  // (configured in FinkitWebModule.AddApiUserFormProperty).
  extraProperties?: Record<string, unknown> | null;
}

// Volo.Abp.Identity.IdentityRoleDto.
export interface IdentityRoleDto {
  id: string;
  name: string;
  isDefault: boolean;
  isStatic: boolean;
  isPublic: boolean;
  concurrencyStamp?: string | null;
}

// Volo.Abp.Identity.IdentityUserCreateDto.
export interface IdentityUserCreateDto {
  userName: string;
  name?: string | null;
  surname?: string | null;
  email: string;
  phoneNumber?: string | null;
  password: string;
  isActive: boolean;
  lockoutEnabled: boolean;
  roleNames: string[];
  // Must carry the required "IsApiUser" bool, else the server rejects the
  // request with "The IsApiUser field is required."
  extraProperties?: Record<string, unknown>;
}

// Volo.Abp.Identity.IdentityUserUpdateDto (password optional = keep current).
export interface IdentityUserUpdateDto {
  userName: string;
  name?: string | null;
  surname?: string | null;
  email: string;
  phoneNumber?: string | null;
  password?: string | null;
  isActive: boolean;
  lockoutEnabled: boolean;
  roleNames: string[];
  concurrencyStamp?: string | null;
  // Carries the required "IsApiUser" bool (see IdentityUserCreateDto).
  extraProperties?: Record<string, unknown>;
}

// Finkit.Identity.SetActiveDto.
export interface SetActiveDto {
  isActive: boolean;
}

// Volo.Abp.Identity.IdentityRoleCreateDto.
export interface IdentityRoleCreateDto {
  name: string;
  isDefault: boolean;
  isPublic: boolean;
}

// Volo.Abp.Identity.IdentityRoleUpdateDto.
export interface IdentityRoleUpdateDto {
  name: string;
  isDefault: boolean;
  isPublic: boolean;
  concurrencyStamp?: string | null;
}

// Finkit.Needs.Pagination.Dtos.PaginatedInputDto : PagedAndSortedResultRequestDto.
// (skipCount/maxResultCount/sorting come from the shared ServerTableRequest.)
export interface IdentityDatatableInput {
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
  draw?: number;
  search?: string;
  name?: string;
}

// ── Permission management (Volo.Abp.PermissionManagement) ──────────────────
// Shapes of GetPermissionListResultDto / UpdatePermissionsDto.

export interface ProviderInfoDto {
  providerName: string;
  providerKey?: string | null;
}

export interface PermissionGrantInfoDto {
  name: string;
  displayName: string;
  parentName?: string | null;
  isGranted: boolean;
  allowedProviders: string[];
  grantedProviders: ProviderInfoDto[];
}

export interface PermissionGroupDto {
  name: string;
  displayName: string;
  displayNameKey?: string | null;
  displayNameResource?: string | null;
  permissions: PermissionGrantInfoDto[];
}

export interface GetPermissionListResultDto {
  entityDisplayName: string;
  groups: PermissionGroupDto[];
}

export interface UpdatePermissionDto {
  name: string;
  isGranted: boolean;
}

export interface UpdatePermissionsDto {
  permissions: UpdatePermissionDto[];
}

// Permission provider keys used by the legacy pages.
export const PERMISSION_PROVIDER_USER = 'U';
export const PERMISSION_PROVIDER_ROLE = 'R';

export const MAX_USERNAME_LENGTH = 256;
export const MAX_EMAIL_LENGTH = 256;
export const MAX_PASSWORD_LENGTH = 128;
export const MAX_NAME_LENGTH = 64;
export const MAX_SURNAME_LENGTH = 64;
export const MAX_PHONE_LENGTH = 16;
export const MAX_ROLE_NAME_LENGTH = 256;
